import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { IRootState } from 'src/reducers';
import selectors from 'src/selectors';
import UserDetails from 'src/models/UserDetails';
import Permissions from 'src/models/Permissions';
import Authorization from 'src/util/Authorization';
import { ILeaseMaster } from 'src/models/dlcLease/ILeaseMaster';
import { ILeaseInvoice } from 'src/models/dlcLease/ILeaseInvoice';
import { ILeaseRecording } from 'src/models/dlcLease/ILeaseRecording';
import * as styles from './styles';
import { Field, DateField } from './Master';
import RecordingModal from './RecordingsModal';
import { ActionButton } from './Header';
import { ICheckType } from 'src/models/dlcLease/ICheckType';
import CreateCollectionCheckModal from './checks/CreateCollectionCheckModal';
import CreateRecordingFeeCheckModal from './checks/CreateRecordingFeeCheckModal';
import LeaseChecksDialog from './checks/ChecksModal';
import SelectCheckTypeModal from './checks/SelectCheckTypeModal';
import CreateLessorDirectCheckModal from './checks/CreateLessorDirectCheckModal';
import CreateWithholdingCheckModal from './checks/CreateWithholdingCheckModal';
import CreateIRSWithholdingCheckModal from './checks/CreateIRSCheckModal';
import LeaseCollectionHistoryModal from './LeaseCollectionHistoryModal';
import LeaseDueDateModal from './LeaseDueDateModal';

import { BiCalculator, BiLayer } from 'react-icons/bi';
import { IInvoiceGroup, ICompanyLookup } from 'src/models/dlcLease/IInvoiceGroup';
import InvoiceGroupModal from './InvoiceGroupModal';
import CreateInvoiceGroupModal from './CreateInvoiceGroupModal';
import PaidByOtherModal from 'src/components/invoicing/components/PaidByOtherModal';
import PostInvoicesModal from './PostInvoicesModal';
import { Loader2 } from 'src/components/common/icons';
import VoidInvoicesModal from './VoidInvoicesModal';
import LessorCsvDownloadButton from './LessorCsvDownloadButton';
import PaymentDueDateCalculatorModal from './PaymentDueDateCalculatorModal';
import ContextMenu from 'src/components/common/ContextMenu';
import { ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';

interface IInvoiceSectionProps {
    leaseMaster: ILeaseMaster;
    onChange?: (field: string, value: unknown) => void;
    onSave?: () => Promise<boolean>;
    onRefresh?: (invoices: ILeaseInvoice[]) => void;
    saveError?: ILeaseRecordsApiError | null;
    currentUser?: UserDetails;
}

interface IStatusBadge {
    label: string;
    color: string;
    bg: string;
    border: string;
}

const calculateInvoiceDueDate = (paymentDueDate: Date | string | undefined): string | undefined => {
    if (!paymentDueDate) {
        return undefined;
    }

    let date: Date;
    if (paymentDueDate instanceof Date) {
        date = new Date(paymentDueDate.getFullYear(), paymentDueDate.getMonth(), paymentDueDate.getDate());
    } else {
        const dateParts = paymentDueDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!dateParts) {
            return undefined;
        }
        date = new Date(Number(dateParts[1]), Number(dateParts[2]) - 1, Number(dateParts[3]));
    }

    if (Number.isNaN(date.getTime())) {
        return undefined;
    }

    let businessDaysSubtracted = 0;
    while (businessDaysSubtracted < 4) {
        date.setDate(date.getDate() - 1);
        const dayOfWeek = date.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            businessDaysSubtracted += 1;
        }
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const InvoiceSection: React.FC<IInvoiceSectionProps> = ({ leaseMaster, onChange, onSave, onRefresh, saveError, currentUser }) => {
    const { get, post, response } = useFetch();
    const canVoidInvoices = Authorization.HasPermission(Permissions.LeaseRecordsVoid, currentUser);

    // Keyed by invoice identity, not list index: refreshInvoices() and the save
    // round-trip both replace the array wholesale, so an index would survive
    // into whichever invoice happens to land in that slot afterwards.
    const [expandedInvoiceKeys, setExpandedInvoiceKeys] = useState<Set<string>>(new Set());
    const [activePopoverIndex, setActivePopoverIndex] = useState<number | null>(null);

    // --- Loading State for Immediate Invoice Creation ---
    const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
    const [createInvoiceError, setCreateInvoiceError] = useState<ILeaseRecordsApiError | null>(null);

    // Modal States
    const [isRecordingModalOpen, setRecordingModalOpen] = useState(false);
    const [isChecksModalOpen, setIsChecksModalOpen] = useState(false);
    const [isPostInvoiceModalOpen, setIsPostInvoiceModalOpen] = useState(false);
    const [isVoidInvoiceModalOpen, setIsVoidInvoiceModalOpen] = useState(false);
    const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);
    const [isDueDateModalOpen, setIsDueDateModalOpen] = useState(false);
    const [paymentDueDateCalculatorIndex, setPaymentDueDateCalculatorIndex] = useState<number | null>(null);

    // --- Invoice Group State ---
    const [isInvoiceGroupViewOpen, setIsInvoiceGroupViewOpen] = useState(false);
    const [isInvoiceGroupCreateOpen, setIsInvoiceGroupCreateOpen] = useState(false);
    const [invoiceGroupData, setInvoiceGroupData] = useState<IInvoiceGroup | null>(null);
    const [invoiceGroups, setInvoiceGroups] = useState<IInvoiceGroup[]>([]);
    const [invoiceGroupIndex, setInvoiceGroupIndex] = useState(0);
    const [companyLookups, setCompanyLookups] = useState<ICompanyLookup[]>([]);

    // Force Render Trigger
    const [, setForceRender] = useState(0);

    // --- Check Creation State ---
    const [isCheckTypesModalOpen, setIsCheckTypesModalOpen] = useState(false);
    const [isCreateCheckModalOpen, setIsCreateCheckModalOpen] = useState(false);
    const [selectedCheckType, setSelectedCheckType] = useState<ICheckType | null>(null);
    const [invoiceForCheckCreation, setInvoiceForCheckCreation] = useState<ILeaseInvoice | null>(null);

    const [selectedInvoiceForModal, setSelectedInvoiceForModal] = useState<ILeaseInvoice | null>(null);

    // --- Paid By Other State ---
    const [showPaidOtherModal, setShowPaidOtherModal] = useState(false);
    const [paidOtherStatusMap, setPaidOtherStatusMap] = useState<Record<number, string>>({});
    const [isSavingInvoice, setIsSavingInvoice] = useState(false);
    const [saveFeedback, setSaveFeedback] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

    const isEditable = !!onChange;
    const invoices = useMemo(() => leaseMaster.invoices || [], [leaseMaster.invoices]);

    useEffect(() => {
        // After a successful save, adopt the reloaded recordings and generated detail
        // IDs. Keep local drafts on a failed save (those invoices still have an operation).
        setSelectedInvoiceForModal((selected) => {
            if (!selected?.liTg) {
                return selected;
            }
            const refreshed = invoices.find((invoice) => invoice.liTg === selected.liTg);
            return refreshed && !refreshed.operation ? refreshed : selected;
        });
    }, [invoices]);

    useEffect(() => {
        if (saveFeedback !== 'saved') {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => setSaveFeedback('idle'), 10000);
        return () => window.clearTimeout(timeoutId);
    }, [saveFeedback]);

    const { bonusSum, recFeeSum, grandTotal, counted } = useMemo(() => {
        const active = (invoices || []).filter((inv) => inv?.operation !== 'delete');
        const bonus = active.reduce((acc, inv) => acc + (inv?.totBonus ?? 0), 0);
        const rec = active.reduce((acc, inv) => acc + (inv?.recordingFee ?? inv?.recFee ?? 0), 0);
        const total = active.reduce((acc, inv) => acc + (inv?.invoiceTotal ?? 0), 0);
        return {
            bonusSum: bonus,
            recFeeSum: rec,
            grandTotal: total || bonus + rec,
            counted: active.length,
        };
    }, [invoices]);

    const fetchAllPaidStatuses = async (): Promise<void> => {
        if (!invoices || invoices.length === 0) {
            return;
        }

        const activeInvoices = invoices.filter((inv) => inv.liTg && inv.operation !== 'delete');
        if (activeInvoices.length === 0) {
            return;
        }

        const newStatusMap: Record<number, string> = {};

        await Promise.all(
            activeInvoices.map(async (inv) => {
                try {
                    const data = await get(`/v1.0/leases/invoices/${inv.liTg}/paid-other`);
                    if (data && data.paidFlag) {
                        newStatusMap[inv.liTg] = data.paidFlag;
                    }
                } catch (err) {
                    // Ignore errors for individual fetch
                }
            })
        );

        setPaidOtherStatusMap(newStatusMap);
    };

    useEffect(() => {
        fetchAllPaidStatuses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leaseMaster.lsNo, invoices.length]);

    const checkPaidStatusSingle = async (liTg: number): Promise<void> => {
        if (!liTg) {
            return;
        }
        const data = await get(`/v1.0/leases/invoices/${liTg}/paid-other`);

        if (response.ok && data && data.paidFlag) {
            setPaidOtherStatusMap((prev) => ({ ...prev, [liTg]: data.paidFlag }));
        } else {
            setPaidOtherStatusMap((prev) => {
                const next = { ...prev };
                delete next[liTg];
                return next;
            });
        }
    };

    const handlePaidOtherSuccess = (): void => {
        if (selectedInvoiceForModal?.liTg) {
            checkPaidStatusSingle(selectedInvoiceForModal.liTg);
        }
    };

    const getPaidStatusBadge = (code: string): IStatusBadge | null => {
        switch (code) {
            case 'O':
                return null;
            case 'Y':
                return { label: 'Paid By Other (Unknown)', color: '#D97706', bg: '#FEF3C7', border: '#FDE68A' };
            case 'Z':
                return { label: 'Zero Bonus', color: '#059669', bg: '#D1FAE5', border: '#A7F3D0' };
            default:
                return null;
        }
    };

    const handleOpenInvoiceGroup = async (): Promise<void> => {
        const wasSaved = await saveInvoiceChanges();
        if (!wasSaved) {
            return;
        }

        const comps = await get('/v1.0/leases/master/companies');
        if (response.ok) {
            const mapped = comps.map((c: Record<string, unknown>) => ({ compId: c.id || c.compId, name: c.name }));
            setCompanyLookups(mapped);
        }

        const groups = await get(`/v1.0/leases/invoice-groups/by-lease/${leaseMaster.lsNo}`);

        if (response.ok && Array.isArray(groups) && groups.length > 0) {
            const orderedGroups = [...groups].sort((a: IInvoiceGroup, b: IInvoiceGroup) => a.groupNo - b.groupNo);
            setInvoiceGroups(orderedGroups);
            setInvoiceGroupIndex(0);
            setInvoiceGroupData(orderedGroups[0]);
        } else {
            setInvoiceGroups([]);
            setInvoiceGroupIndex(0);
            setInvoiceGroupData(null);
        }

        setIsInvoiceGroupViewOpen(true);
    };

    const handleSwitchToCreateGroup = (): void => {
        setIsInvoiceGroupViewOpen(false);
        setIsInvoiceGroupCreateOpen(true);
    };

    const handleGroupCreated = async (newGroupNo: number): Promise<void> => {
        setIsInvoiceGroupCreateOpen(false);
        const groups = await get(`/v1.0/leases/invoice-groups/by-lease/${leaseMaster.lsNo}`);

        if (response.ok && Array.isArray(groups)) {
            const orderedGroups = [...groups].sort((a: IInvoiceGroup, b: IInvoiceGroup) => a.groupNo - b.groupNo);
            const newIndex = Math.max(
                0,
                orderedGroups.findIndex((group: IInvoiceGroup) => group.groupNo === newGroupNo)
            );
            setInvoiceGroups(orderedGroups);
            setInvoiceGroupIndex(newIndex);
            setInvoiceGroupData(orderedGroups[newIndex] || null);
            setIsInvoiceGroupViewOpen(true);
        } else {
            setCreateInvoiceError(
                parseLeaseRecordsApiError({ title: 'Group created, but failed to load details. Please try opening Invoice Group again.' })
            );
        }
    };

    const handleInvoiceGroupNavigate = (index: number): void => {
        if (index < 0 || index >= invoiceGroups.length) {
            return;
        }
        setInvoiceGroupIndex(index);
        setInvoiceGroupData(invoiceGroups[index]);
    };

    const handleInvoiceGroupDeleted = (groupNo: number): void => {
        const remainingGroups = invoiceGroups.filter((group) => group.groupNo !== groupNo);
        setInvoiceGroups(remainingGroups);

        if (remainingGroups.length === 0) {
            setInvoiceGroupIndex(0);
            setInvoiceGroupData(null);
            return;
        }

        const nextIndex = Math.min(invoiceGroupIndex, remainingGroups.length - 1);
        setInvoiceGroupIndex(nextIndex);
        setInvoiceGroupData(remainingGroups[nextIndex]);
    };

    const saveInvoiceChanges = async (): Promise<boolean> => {
        setIsSavingInvoice(true);
        setSaveFeedback('saving');
        try {
            const wasSaved = (await onSave?.()) ?? true;
            setSaveFeedback(wasSaved ? 'saved' : 'error');
            return wasSaved;
        } catch {
            setSaveFeedback('error');
            return false;
        } finally {
            setIsSavingInvoice(false);
        }
    };

    const openAfterInvoiceSave = async (openDialog: () => void): Promise<void> => {
        const wasSaved = await saveInvoiceChanges();
        if (wasSaved) {
            openDialog();
        }
    };

    const handleCommentsSave = async (invoiceIndex: number, comments: { comm1?: string; comm2?: string; attach?: string }): Promise<boolean> => {
        if (!onChange) {
            return false;
        }

        const updatedInvoices = invoices.map((invoice, i) => {
            if (i !== invoiceIndex) {
                return invoice;
            }
            return {
                ...invoice,
                ...comments,
                operation: invoice.liTg ? 'update' : 'add',
            };
        });

        onChange('invoices', updatedInvoices);
        return saveInvoiceChanges();
    };

    const formatCurrency = (amount?: number): string => {
        if (amount === undefined || amount === null) {
            return '';
        }
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
        }).format(amount);
    };

    // Single source of truth for invoice identity -- used for both the React key
    // and the expanded-set membership so the two can never disagree.
    const getInvoiceKey = (invoice: ILeaseInvoice, index: number): string => (invoice.liTg ? String(invoice.liTg) : `new-${index}`);

    const toggleInvoiceExpanded = async (key: string): Promise<void> => {
        if (expandedInvoiceKeys.has(key)) {
            const wasSaved = await saveInvoiceChanges();
            if (!wasSaved) {
                return;
            }
        }

        setExpandedInvoiceKeys((prev) => {
            const next = new Set(prev);
            if (!next.delete(key)) {
                next.add(key);
            }
            return next;
        });
    };

    const toggleAllInvoices = async (): Promise<void> => {
        if (allExpanded) {
            const wasSaved = await saveInvoiceChanges();
            if (!wasSaved) {
                return;
            }
            setExpandedInvoiceKeys(new Set());
            return;
        }

        setExpandedInvoiceKeys(new Set(expandableKeys));
    };

    // Removed invoices are still in the array (soft-deleted) but cannot be opened.
    // Map before filtering: getInvoiceKey falls back to the list index for drafts,
    // which must be the index the render loop sees, not the post-filter one.
    const expandableKeys = invoices
        .map((inv, i) => (inv?.operation !== 'delete' ? getInvoiceKey(inv, i) : null))
        .filter((key): key is string => key !== null);
    const allExpanded = expandableKeys.length > 0 && expandableKeys.every((k) => expandedInvoiceKeys.has(k));

    const isInvoicePosted = (invoice: ILeaseInvoice): boolean => !!invoice.arDate;

    // An invoice with no assigned number is still a draft -- this is the same
    // condition that renders the "#N" badge and the "Invoice New" title.
    const isInvoiceDraft = (invoice: ILeaseInvoice): boolean => !invoice.invoiceNo1;

    // State is carried by the "Posted"/"Voided" badges and the number-chip border
    // color. Order matters: an invoice can be both voided and posted, and voided
    // is what the user needs to see first.
    const getInvoiceAccent = (invoice: ILeaseInvoice): string => {
        if (invoice.voidDate) {
            return '#DC2626';
        }
        if (isInvoicePosted(invoice)) {
            return '#D97706';
        }
        if (isInvoiceDraft(invoice)) {
            return styles.colors.primaryLight;
        }
        return styles.colors.gray300;
    };

    // Posted invoices are locked. Communicate that with a standard disabled look --
    // a light gray background and muted text -- kept legible for reading values,
    // with a soft amber border so posted fields still read as posted at a glance.
    const postedInputStyle: React.CSSProperties = {
        backgroundColor: styles.colors.gray50,
        borderColor: '#F3C983',
        color: styles.colors.gray700,
        fontWeight: styles.typography.fontWeight.medium,
        cursor: 'not-allowed',
    };

    const handleInvoiceChange = (index: number, field: string, value: unknown): void => {
        if (!onChange) {
            return;
        }
        setSaveFeedback('idle');

        const updatedInvoices = [...invoices];
        const updatedInvoice: ILeaseInvoice = {
            ...updatedInvoices[index],
            [field]: value,
            operation: updatedInvoices[index].liTg ? 'update' : 'add',
        };
        if (field === 'dueDate1') {
            updatedInvoice.dueDate2 = calculateInvoiceDueDate(value as Date | string | undefined);
        }
        updatedInvoices[index] = updatedInvoice;
        onChange('invoices', updatedInvoices);
    };

    const refreshInvoices = async (): Promise<void> => {
        if ((!onChange && !onRefresh) || !leaseMaster.lsNo) {
            return;
        }

        try {
            const freshInvoices = await get(`/v1.0/leases/invoices/by-lsNo/${leaseMaster.lsNo}`);
            if (response.ok && Array.isArray(freshInvoices)) {
                if (onRefresh) {
                    onRefresh(freshInvoices);
                } else {
                    onChange?.('invoices', freshInvoices);
                }
            }
        } catch {
            setSaveFeedback('error');
        }
    };

    const handleSaveRecordings = async (updatedRecordings: ILeaseRecording[]): Promise<boolean> => {
        if (!onChange || !selectedInvoiceForModal) {
            return false;
        }

        const invoiceIndex = invoices.findIndex((inv) => inv.liTg === selectedInvoiceForModal.liTg);
        if (invoiceIndex === -1) {
            return false;
        }

        const updatedInvoices = [...invoices];
        updatedInvoices[invoiceIndex] = {
            ...updatedInvoices[invoiceIndex],
            recordings: updatedRecordings,
            operation: updatedInvoices[invoiceIndex].liTg ? 'update' : 'add',
        };

        onChange('invoices', updatedInvoices);
        return saveInvoiceChanges();
    };

    const handleCreateEmptyInvoice = async (): Promise<void> => {
        if (!leaseMaster.lsNo) {
            return;
        }

        setIsCreatingInvoice(true);
        setCreateInvoiceError(null);
        try {
            const newInv = await post(`/v1.0/leases/invoices`, { lsNo: leaseMaster.lsNo });

            if (response.ok && newInv) {
                const updatedInvoices = [...invoices, newInv];
                if (onRefresh) {
                    onRefresh(updatedInvoices);
                } else {
                    onChange?.('invoices', updatedInvoices);
                }

                // Auto-expand the invoice we just created, leaving any others open.
                setExpandedInvoiceKeys((prev) => new Set(prev).add(getInvoiceKey(newInv, updatedInvoices.length - 1)));
                setForceRender((prev) => prev + 1);
            } else {
                setCreateInvoiceError(parseLeaseRecordsApiError(newInv || response.data, response.status));
            }
        } catch {
            setCreateInvoiceError(parseLeaseRecordsApiError(response.data, response.status || 500));
        } finally {
            setIsCreatingInvoice(false);
        }
    };

    const handlePromoteInvoiceShell = async (invoice: ILeaseInvoice): Promise<void> => {
        if (!leaseMaster.lsNo || !invoice.liTg || invoice.invoiceNo1) {
            return;
        }

        setIsCreatingInvoice(true);
        setCreateInvoiceError(null);
        try {
            const numberedInvoice = await post('/v1.0/leases/invoices', {
                lsNo: leaseMaster.lsNo,
                liTg: invoice.liTg,
            });

            if (response.ok && numberedInvoice) {
                const updatedInvoices = invoices.map((existing) => (existing.liTg === invoice.liTg ? numberedInvoice : existing));
                if (onRefresh) {
                    onRefresh(updatedInvoices);
                } else {
                    onChange?.('invoices', updatedInvoices);
                }
            } else {
                setCreateInvoiceError(parseLeaseRecordsApiError(numberedInvoice || response.data, response.status));
            }
        } catch {
            setCreateInvoiceError(parseLeaseRecordsApiError(response.data, response.status || 500));
        } finally {
            setIsCreatingInvoice(false);
        }
    };

    const handleCheckCreated = (): void => {
        void refreshInvoices();
        void fetchAllPaidStatuses();
    };

    const handleRemoveInvoice = async (index: number): Promise<void> => {
        if (!onChange || !confirm('Are you sure you want to remove this invoice?')) {
            return;
        }

        const updatedInvoices = [...invoices];
        updatedInvoices[index] = {
            ...updatedInvoices[index],
            operation: 'delete',
        };

        onChange('invoices', updatedInvoices);
        const wasSaved = await saveInvoiceChanges();
        if (!wasSaved) {
            return;
        }

        // Collapse only the invoice being removed; anything else stays open.
        const removedKey = getInvoiceKey(updatedInvoices[index], index);
        setExpandedInvoiceKeys((prev) => {
            const next = new Set(prev);
            next.delete(removedKey);
            return next;
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, key: string): void => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            void toggleInvoiceExpanded(key);
        }
    };

    const handleInitCheckCreation = async (invoice: ILeaseInvoice): Promise<void> => {
        await openAfterInvoiceSave(() => {
            setInvoiceForCheckCreation(invoice);
            setIsCheckTypesModalOpen(true);
        });
    };

    const onTypeSelected = (type: ICheckType): void => {
        setSelectedCheckType(type);
        setIsCheckTypesModalOpen(false);
        setIsCreateCheckModalOpen(true);
    };

    return (
        <div style={styles.sectionContainer}>
            <h2 style={styles.sectionHeader}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={styles.colors.primary} strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10 9 9 9 8 9" />
                    </svg>
                    Invoices
                </span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {saveFeedback === 'saving' && (
                        <span style={{ alignSelf: 'center', color: styles.colors.primary, fontSize: styles.typography.fontSize.sm }}>
                            Autosave pending...
                        </span>
                    )}
                    {saveFeedback === 'saved' && (
                        <span style={{ alignSelf: 'center', color: styles.colors.success, fontSize: styles.typography.fontSize.sm }}>
                            Invoice saved
                        </span>
                    )}
                    {saveFeedback === 'error' && (
                        <span role="alert" style={{ alignSelf: 'center', color: styles.colors.danger, fontSize: styles.typography.fontSize.sm }}>
                            Invoice save failed; action blocked
                        </span>
                    )}
                    <button
                        onClick={handleOpenInvoiceGroup}
                        style={{
                            ...styles.buttonSecondary,
                            fontSize: styles.typography.fontSize.sm,
                            padding: '0.5rem 1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                        }}
                    >
                        <BiLayer /> Invoice Group
                    </button>

                    <button
                        onClick={() => setIsChecksModalOpen(true)}
                        style={{
                            ...styles.buttonSecondary,
                            fontSize: styles.typography.fontSize.sm,
                            padding: '0.5rem 1rem',
                        }}
                    >
                        Display Checks
                    </button>
                    {canVoidInvoices && (
                        <button
                            onClick={() => void openAfterInvoiceSave(() => setIsVoidInvoiceModalOpen(true))}
                            style={{
                                ...styles.buttonSecondary,
                                fontSize: styles.typography.fontSize.sm,
                                padding: '0.5rem 1rem',
                            }}
                        >
                            Void Invoice
                        </button>
                    )}
                    <button
                        onClick={() => void openAfterInvoiceSave(() => setIsPostInvoiceModalOpen(true))}
                        disabled={isSavingInvoice}
                        style={{
                            ...styles.buttonSecondary,
                            fontSize: styles.typography.fontSize.sm,
                            padding: '0.5rem 1rem',
                            opacity: isSavingInvoice ? 0.7 : 1,
                        }}
                    >
                        {isSavingInvoice ? 'Saving before post...' : 'Post Invoice'}
                    </button>
                    {isEditable && (
                        <button
                            onClick={handleCreateEmptyInvoice}
                            disabled={isCreatingInvoice}
                            style={{
                                ...styles.buttonPrimary,
                                fontSize: styles.typography.fontSize.sm,
                                padding: '0.5rem 1rem',
                                opacity: isCreatingInvoice ? 0.7 : 1,
                                cursor: isCreatingInvoice ? 'not-allowed' : 'pointer',
                            }}
                            onMouseEnter={(e) => {
                                if (!isCreatingInvoice) {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = styles.shadows.md;
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isCreatingInvoice) {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }
                            }}
                        >
                            {isCreatingInvoice ? (
                                <Loader2 className="animate-spin" style={{ width: '16px', height: '16px', marginRight: '0.5rem' }} />
                            ) : (
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    style={{ marginRight: '0.5rem' }}
                                >
                                    <line x1="12" y1="5" x2="12" y2="19" />
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                </svg>
                            )}
                            Add Invoice
                        </button>
                    )}
                </div>
            </h2>

            <LeaseRecordsErrorAlert error={createInvoiceError} onDismiss={() => setCreateInvoiceError(null)} />

            {invoices.length === 0 && (
                <div style={styles.emptyState}>
                    <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={styles.colors.gray400}
                        strokeWidth="1.5"
                        style={{ margin: '0 auto 1rem' }}
                    >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <p style={{ margin: 0, color: styles.colors.gray600 }}>No invoice data available</p>
                    {isEditable && (
                        <p style={{ marginTop: '0.5rem', fontSize: styles.typography.fontSize.sm, color: styles.colors.gray500 }}>
                            Click &quot;Add Invoice&quot; to create your first invoice
                        </p>
                    )}
                </div>
            )}

            <div
                style={{
                    ...styles.cardBase,
                    marginTop: '1.5rem',
                    border: `1px solid ${styles.colors.gray200}`,
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.75rem',
                    }}
                >
                    <h3
                        style={{
                            margin: 0,
                            fontSize: styles.typography.fontSize.lg,
                            color: styles.colors.gray900,
                            fontWeight: styles.typography.fontWeight.semibold,
                        }}
                    >
                        Totals {counted > 0 ? `(${counted} invoice${counted === 1 ? '' : 's'})` : ''}
                    </h3>
                    {expandableKeys.length > 1 && (
                        <button type="button" style={styles.buttonSecondary} onClick={() => void toggleAllInvoices()}>
                            {allExpanded ? 'Collapse All' : 'Expand All'}
                        </button>
                    )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                    <div style={styles.fieldContainer}>
                        <label style={styles.fieldLabel}>Total Bonus</label>
                        <div style={styles.inputDisabled}>{formatCurrency(bonusSum)}</div>
                    </div>
                    <div style={styles.fieldContainer}>
                        <label style={styles.fieldLabel}>Total Recording Fees</label>
                        <div style={styles.inputDisabled}>{formatCurrency(recFeeSum)}</div>
                    </div>
                    <div style={styles.fieldContainer}>
                        <label style={styles.fieldLabel}>Grand Total</label>
                        <div
                            style={{
                                ...styles.inputDisabled,
                                fontWeight: styles.typography.fontWeight.semibold,
                            }}
                        >
                            {formatCurrency(grandTotal)}
                        </div>
                    </div>
                </div>
            </div>

            {/* --- MODALS --- */}

            <PostInvoicesModal
                isOpen={isPostInvoiceModalOpen}
                onClose={() => setIsPostInvoiceModalOpen(false)}
                lsNo={leaseMaster.lsNo}
                onSuccess={() => {
                    refreshInvoices();
                    fetchAllPaidStatuses();
                }}
            />

            {canVoidInvoices && (
                <VoidInvoicesModal
                    isOpen={isVoidInvoiceModalOpen}
                    onClose={() => setIsVoidInvoiceModalOpen(false)}
                    lsNo={leaseMaster.lsNo}
                    onSuccess={() => {
                        refreshInvoices();
                        fetchAllPaidStatuses();
                    }}
                />
            )}

            <LeaseChecksDialog leaseMaster={leaseMaster} isOpen={isChecksModalOpen} onClose={() => setIsChecksModalOpen(false)} />

            <SelectCheckTypeModal onTypeSelected={onTypeSelected} isOpen={isCheckTypesModalOpen} onClose={() => setIsCheckTypesModalOpen(false)} />

            <CreateLessorDirectCheckModal
                isOpen={selectedCheckType?.type === '1' && isCreateCheckModalOpen}
                onClose={() => setIsCreateCheckModalOpen(false)}
                onSuccess={handleCheckCreated}
                leaseMaster={leaseMaster}
                invoice={invoiceForCheckCreation}
            />

            <CreateCollectionCheckModal
                isOpen={selectedCheckType?.type === '2' && isCreateCheckModalOpen}
                onClose={() => setIsCreateCheckModalOpen(false)}
                onSuccess={handleCheckCreated}
                leaseMaster={leaseMaster}
            />

            <CreateRecordingFeeCheckModal
                isOpen={selectedCheckType?.type === '3' && isCreateCheckModalOpen}
                onClose={() => setIsCreateCheckModalOpen(false)}
                onSuccess={handleCheckCreated}
                leaseMaster={leaseMaster}
            />

            <CreateWithholdingCheckModal
                isOpen={selectedCheckType?.type === '7' && isCreateCheckModalOpen}
                onClose={() => setIsCreateCheckModalOpen(false)}
                onSuccess={handleCheckCreated}
                leaseMaster={leaseMaster}
                invoice={invoiceForCheckCreation}
            />

            <CreateIRSWithholdingCheckModal
                isOpen={selectedCheckType?.type === '8' && isCreateCheckModalOpen}
                onClose={() => setIsCreateCheckModalOpen(false)}
                onSuccess={handleCheckCreated}
                leaseMaster={leaseMaster}
            />

            <InvoiceGroupModal
                isOpen={isInvoiceGroupViewOpen}
                onClose={() => setIsInvoiceGroupViewOpen(false)}
                data={invoiceGroupData}
                companies={companyLookups}
                onCreateClick={handleSwitchToCreateGroup}
                relatedGroups={invoiceGroups}
                currentGroupIndex={invoiceGroupIndex}
                onNavigate={handleInvoiceGroupNavigate}
                onDeleted={handleInvoiceGroupDeleted}
            />

            <CreateInvoiceGroupModal
                isOpen={isInvoiceGroupCreateOpen}
                onClose={() => setIsInvoiceGroupCreateOpen(false)}
                initialClNo={leaseMaster.clNo}
                initialPrNo={leaseMaster.prNo}
                onSuccess={handleGroupCreated}
            />

            <PaidByOtherModal
                isOpen={showPaidOtherModal}
                onClose={() => setShowPaidOtherModal(false)}
                liTg={selectedInvoiceForModal?.liTg || null}
                onSuccess={handlePaidOtherSuccess}
            />

            {/* --- INVOICE LIST --- */}

            {invoices.map((invoice, index) => {
                const commentCount = [invoice.comm1, invoice.comm2, invoice.attach].filter(Boolean).length;
                const paidTypeCode = invoice.liTg ? paidOtherStatusMap[invoice.liTg] : null;
                const badge = paidTypeCode ? getPaidStatusBadge(paidTypeCode) : null;
                const isPosted = isInvoicePosted(invoice);
                const isDeleted = invoice.operation === 'delete';
                const invoiceKey = getInvoiceKey(invoice, index);
                const isExpanded = expandedInvoiceKeys.has(invoiceKey);
                const accent = getInvoiceAccent(invoice);
                const invoicePalette = styles.leaseAccentSequence[index % styles.leaseAccentSequence.length];
                const bandColor = isDeleted ? styles.colors.gray100 : invoicePalette.backgroundColor;
                const postedFieldStyle = isPosted ? postedInputStyle : undefined;
                const canEditUnposted = isEditable && !isPosted;
                const calculatedInvoiceDisplayAmount = (invoice.total ?? 0) + (invoice.recordingFee ?? invoice.recFee ?? 0);
                const invoiceDisplayAmount =
                    invoice.invoiceTotal ??
                    (calculatedInvoiceDisplayAmount > 0
                        ? calculatedInvoiceDisplayAmount
                        : invoice.recordingFee ?? invoice.recFee ?? invoice.totBonus);

                return (
                    <div
                        key={invoiceKey}
                        style={{
                            ...styles.cardBase,
                            cursor: 'pointer',
                            marginBottom: '1rem',
                            padding: 0,
                            overflow: 'hidden',
                            backgroundColor: isDeleted ? styles.colors.gray50 : invoicePalette.backgroundColor,
                            border: `1px solid ${isExpanded && !isDeleted ? invoicePalette.borderLeftColor : styles.colors.gray300}`,
                            borderLeft: `4px solid ${isDeleted ? styles.colors.danger : invoicePalette.borderLeftColor}`,
                            boxShadow: isExpanded && !isDeleted ? styles.leaseSectionFrameActive.boxShadow : styles.shadows.sm,
                            opacity: isDeleted ? 0.6 : 1,
                        }}
                        onMouseEnter={(e) => {
                            if (!isDeleted) {
                                e.currentTarget.style.boxShadow = styles.shadows.md;
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow =
                                isExpanded && !isDeleted ? (styles.leaseSectionFrameActive.boxShadow as string) : styles.shadows.sm;
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '0.4rem 0.75rem',
                                backgroundColor: bandColor,
                                borderBottom: isExpanded ? `1px solid ${styles.colors.gray300}` : 'none',
                            }}
                            onClick={() => void toggleInvoiceExpanded(invoiceKey)}
                            onKeyDown={(e) => handleKeyDown(e, invoiceKey)}
                            role="button"
                            aria-expanded={isExpanded}
                            tabIndex={0}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <div
                                    style={{
                                        minWidth: '56px',
                                        height: '30px',
                                        flexShrink: 0,
                                        padding: '0 0.6rem',
                                        borderRadius: '6px',
                                        // Solid white against the card tint, so the number chip stays
                                        // legible whether the card body is amber, red, blue, or white.
                                        // Width grows with the number (e.g. #137364A) instead of clipping.
                                        backgroundColor: styles.colors.white,
                                        border: `1px solid ${isDeleted ? styles.colors.gray300 : accent}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: styles.typography.fontSize.base,
                                            fontWeight: styles.typography.fontWeight.bold,
                                            color: styles.colors.primary,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {invoice.invoiceNo1
                                            ? `#${invoice.invoiceNo1}${invoice.invoiceAdd || ''}`
                                            : paidTypeCode === 'O'
                                            ? 'Paid by Other'
                                            : 'Unnumbered shell'}
                                    </span>
                                </div>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                        {/* The number chip carries invoice identity; the headline is the
                                            amount -- the value the user actually scans for. */}
                                        <div
                                            style={{
                                                fontSize: styles.typography.fontSize.base,
                                                fontWeight: styles.typography.fontWeight.semibold,
                                                color: invoiceDisplayAmount ? styles.colors.gray900 : styles.colors.gray500,
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {formatCurrency(invoiceDisplayAmount) || 'No amount set'}
                                        </div>

                                        {invoice.voidDate && (
                                            <span
                                                style={{
                                                    backgroundColor: '#DC2626',
                                                    color: '#FFFFFF',
                                                    padding: '4px 12px',
                                                    borderRadius: '16px',
                                                    fontSize: '0.85rem',
                                                    fontWeight: 600,
                                                    lineHeight: 1,
                                                    whiteSpace: 'nowrap',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                Voided {new Date(invoice.voidDate).toLocaleDateString('en-US')}
                                            </span>
                                        )}

                                        {isPosted && (
                                            <span
                                                style={{
                                                    backgroundColor: '#F59E0B',
                                                    color: styles.colors.gray900,
                                                    border: '1px solid #D97706',
                                                    padding: '4px 12px',
                                                    borderRadius: '16px',
                                                    fontSize: '0.85rem',
                                                    fontWeight: 600,
                                                    lineHeight: 1,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                Posted to A/R
                                            </span>
                                        )}

                                        {/* --- BADGE IN HEADER (Bigger) --- */}
                                        {badge && (
                                            <span
                                                style={{
                                                    backgroundColor: badge.bg,
                                                    color: badge.color,
                                                    border: `1px solid ${badge.border}`,
                                                    padding: '4px 12px',
                                                    borderRadius: '16px',
                                                    fontSize: '0.85rem',
                                                    fontWeight: 600,
                                                    lineHeight: 1,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {badge.label}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                {isEditable && invoice.operation !== 'delete' && (
                                    <ActionButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            void openAfterInvoiceSave(() => {
                                                setSelectedInvoiceForModal(invoice);
                                                setIsCollectionModalOpen(true);
                                            });
                                        }}
                                        label={'Update Collection'}
                                    />
                                )}

                                {isEditable && invoice.operation !== 'delete' && (
                                    <ActionButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            void openAfterInvoiceSave(() => {
                                                setSelectedInvoiceForModal(invoice);
                                                setIsDueDateModalOpen(true);
                                            });
                                        }}
                                        label={'Ext. Due Date'}
                                    />
                                )}

                                {isEditable && invoice.operation !== 'delete' && (
                                    <ActionButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            void handleInitCheckCreation(invoice);
                                        }}
                                        label={'Create Check'}
                                    />
                                )}

                                {isEditable && invoice.operation && invoice.operation !== 'delete' && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            void saveInvoiceChanges();
                                        }}
                                        disabled={isSavingInvoice}
                                        style={{
                                            ...styles.buttonSuccess,
                                            fontSize: styles.typography.fontSize.sm,
                                            padding: '0.375rem 0.75rem',
                                            opacity: isSavingInvoice ? 0.7 : 1,
                                        }}
                                    >
                                        {isSavingInvoice ? 'Saving...' : 'Save Invoice'}
                                    </button>
                                )}
                                {isEditable && invoice.operation !== 'delete' && !isPosted && (
                                    <div style={{ flex: '0 0 3rem', width: '3rem', height: '1.6rem' }}>
                                        <ContextMenu
                                            isVisible={true}
                                            actions={[
                                                {
                                                    text: 'Remove',
                                                    action: () => void handleRemoveInvoice(index),
                                                },
                                            ]}
                                        />
                                    </div>
                                )}
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke={styles.colors.gray500}
                                    strokeWidth="2"
                                    style={{
                                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s ease',
                                    }}
                                >
                                    <polyline points="6 9 12 15 18 9" />
                                </svg>
                            </div>
                        </div>

                        {isExpanded && (
                            <div style={{ backgroundColor: styles.colors.white, padding: '1.5rem 0.75rem' }}>
                                {/* Invoice details grid */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: '1rem 1.25rem' }}>
                                    <DateField
                                        label="Lease IH Date"
                                        value={invoice.ihDate}
                                        editable={isEditable}
                                        onChange={(value) => handleInvoiceChange(index, 'ihDate', value)}
                                    />
                                    <Field
                                        label="Invoice #"
                                        value={invoice.invoiceNo1?.toString() || ''}
                                        type="number"
                                        editable={canEditUnposted && !isInvoiceDraft(invoice)}
                                        inputStyle={postedFieldStyle}
                                        onChange={(value) => handleInvoiceChange(index, 'invoiceNo1', value ? parseInt(value, 10) : undefined)}
                                    />
                                    <Field
                                        label="Invoice Suffix"
                                        value={invoice.invoiceAdd || ''}
                                        editable={canEditUnposted}
                                        inputStyle={postedFieldStyle}
                                        onChange={(value) => handleInvoiceChange(index, 'invoiceAdd', value)}
                                    />
                                    <DateField
                                        label="Invoice Date"
                                        value={invoice.invDate}
                                        editable={canEditUnposted}
                                        inputStyle={postedFieldStyle}
                                        onChange={(value) => handleInvoiceChange(index, 'invDate', value)}
                                    />
                                    <DateField
                                        label="Invoice Due Date"
                                        value={invoice.dueDate2}
                                        editable={canEditUnposted}
                                        inputStyle={postedFieldStyle}
                                        onChange={(value) => handleInvoiceChange(index, 'dueDate2', value)}
                                    />
                                    <DateField
                                        label="Payment Due Date"
                                        value={invoice.dueDate1}
                                        editable={canEditUnposted}
                                        inputStyle={postedFieldStyle}
                                        onChange={(value) => handleInvoiceChange(index, 'dueDate1', value)}
                                        labelAction={
                                            canEditUnposted ? (
                                                <button
                                                    type="button"
                                                    title="Calculate Payment Due Date"
                                                    aria-label="Calculate Payment Due Date"
                                                    onClick={(event) => {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        setPaymentDueDateCalculatorIndex(index);
                                                    }}
                                                    style={{
                                                        border: 0,
                                                        padding: 0,
                                                        background: 'transparent',
                                                        color: styles.colors.primary,
                                                        cursor: 'pointer',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <BiCalculator aria-hidden="true" style={{ width: '1.15rem', height: '1.15rem' }} />
                                                </button>
                                            ) : undefined
                                        }
                                    />

                                    <DateField
                                        label="Arrival Date"
                                        value={invoice.leaseIhDate}
                                        editable={isEditable}
                                        onChange={(value) => handleInvoiceChange(index, 'leaseIhDate', value)}
                                    />
                                    <Field
                                        label="Collect No."
                                        value={invoice.collection1?.toString() || ''}
                                        editable={isEditable}
                                        onChange={(value) => handleInvoiceChange(index, 'collection1', value)}
                                    />
                                    <Field
                                        label="Check Number"
                                        value={invoice.check1?.toString() || ''}
                                        type="number"
                                        editable={false}
                                        onChange={(value) => handleInvoiceChange(index, 'check1', value ? parseInt(value, 10) : undefined)}
                                    />
                                    <DateField
                                        label="Check Date"
                                        value={invoice.chkDate1}
                                        editable={false}
                                        onChange={(value) => handleInvoiceChange(index, 'chkDate1', value)}
                                    />
                                    <DateField
                                        label="Check Sent Date"
                                        value={invoice.checkSentDate}
                                        editable={isEditable}
                                        onChange={(value) => handleInvoiceChange(index, 'checkSentDate', value)}
                                    />
                                    <Field
                                        label="Recording Fee"
                                        value={invoice.recordingFee?.toString() || ''}
                                        type="number"
                                        prefix="$"
                                        editable={false}
                                        onChange={(value) => handleInvoiceChange(index, 'recordingFee', value ? parseFloat(value) : undefined)}
                                    />
                                </div>

                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                                        gap: '1rem 1.25rem',
                                        marginTop: '1.5rem',
                                        borderTop: `1px solid ${styles.colors.border}`,
                                        paddingTop: '1.5rem',
                                    }}
                                >
                                    <Field
                                        label="Bonus"
                                        value={invoice.totBonus?.toString() || ''}
                                        type="number"
                                        prefix="$"
                                        editable={canEditUnposted}
                                        inputStyle={postedFieldStyle}
                                        onChange={(value) => handleInvoiceChange(index, 'totBonus', value ? parseFloat(value) : undefined)}
                                    />
                                    <Field
                                        label="Rent"
                                        value={invoice.advRental?.toString() || ''}
                                        type="number"
                                        prefix="$"
                                        editable={canEditUnposted}
                                        inputStyle={postedFieldStyle}
                                        onChange={(value) => handleInvoiceChange(index, 'advRental', value ? parseFloat(value) : undefined)}
                                    />
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        <Field
                                            label="Other-1"
                                            value={invoice.other?.toString() || ''}
                                            type="number"
                                            prefix="$"
                                            editable={canEditUnposted}
                                            inputStyle={postedFieldStyle}
                                            onChange={(value) => handleInvoiceChange(index, 'other', value ? parseFloat(value) : undefined)}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        <Field
                                            label="Other-2"
                                            value={invoice.addOther?.toString() || ''}
                                            type="number"
                                            prefix="$"
                                            editable={canEditUnposted}
                                            inputStyle={postedFieldStyle}
                                            onChange={(value) => handleInvoiceChange(index, 'addOther', value ? parseFloat(value) : undefined)}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        <Field
                                            label="Other-3"
                                            value={invoice.other3?.toString() || ''}
                                            type="number"
                                            prefix="$"
                                            editable={canEditUnposted}
                                            inputStyle={postedFieldStyle}
                                            onChange={(value) => handleInvoiceChange(index, 'other3', value ? parseFloat(value) : undefined)}
                                        />
                                    </div>
                                </div>

                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(5, 1fr)',
                                        gap: '1rem 1.5rem',
                                        marginTop: '1.5rem',
                                        borderTop: `1px solid ${styles.colors.border}`,
                                        paddingTop: '1.5rem',
                                    }}
                                >
                                    <DateField
                                        label="A/R Date"
                                        value={invoice.arDate}
                                        editable={false}
                                        inputStyle={postedFieldStyle}
                                        onChange={(value) => handleInvoiceChange(index, 'arDate', value)}
                                    />
                                    <Field
                                        label="Funded Amt"
                                        value={invoice.fundedAmount?.toString() || ''}
                                        type="number"
                                        prefix="$"
                                        editable={false}
                                        onChange={(value) => handleInvoiceChange(index, 'fundedAmount', value ? parseFloat(value) : undefined)}
                                    />
                                    <DateField
                                        label="Date Funded"
                                        value={invoice.dateFunded}
                                        editable={false}
                                        onChange={(value) => handleInvoiceChange(index, 'dateFunded', value)}
                                    />
                                    <Field label="Client Check No." value={invoice.checkNoPaid?.toString() || ''} editable={false} />
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        <Field label="Total" value={invoice.total?.toString() || ''} type="number" prefix="$" editable={false} />
                                        <Field
                                            label="Invoice Total"
                                            value={invoice.invoiceTotal?.toString() || ''}
                                            type="number"
                                            prefix="$"
                                            editable={false}
                                        />
                                    </div>
                                </div>

                                {invoice && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: '0.5rem',
                                            marginTop: '1.5rem',
                                            paddingTop: '1.5rem',
                                            borderTop: `1px solid ${styles.colors.border}`,
                                        }}
                                    >
                                        <div style={{ position: 'relative' }}>
                                            <button
                                                style={{ ...styles.buttonSecondary, position: 'relative' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActivePopoverIndex(activePopoverIndex === index ? null : index);
                                                }}
                                                aria-label="Toggle comments"
                                            >
                                                Comments
                                                {commentCount > 0 && (
                                                    <span
                                                        style={{
                                                            position: 'absolute',
                                                            top: '-7px',
                                                            right: '-7px',
                                                            backgroundColor: styles.colors.danger,
                                                            color: 'white',
                                                            borderRadius: '50%',
                                                            minWidth: '18px',
                                                            height: '18px',
                                                            padding: '0 4px',
                                                            fontSize: '10px',
                                                            fontWeight: 'bold',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            border: '1px solid white',
                                                            boxSizing: 'border-box',
                                                        }}
                                                    >
                                                        {commentCount}
                                                    </span>
                                                )}
                                            </button>
                                            {activePopoverIndex === index && (
                                                <CommentsPopover
                                                    invoice={invoice}
                                                    isEditable={isEditable}
                                                    onClose={() => setActivePopoverIndex(null)}
                                                    onSave={(comments) => handleCommentsSave(index, comments)}
                                                />
                                            )}
                                        </div>

                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            {isInvoiceDraft(invoice) && isEditable && (
                                                <button
                                                    style={styles.buttonPrimary}
                                                    disabled={isCreatingInvoice}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        void handlePromoteInvoiceShell(invoice);
                                                    }}
                                                >
                                                    {isCreatingInvoice ? 'Creating Invoice...' : 'Create Invoice'}
                                                </button>
                                            )}

                                            {/* NEW: Paid By Other Button */}
                                            {(invoice.invoiceNo1 === null || invoice.invoiceNo1 === undefined) && (
                                                <button
                                                    style={styles.buttonSecondary}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        void openAfterInvoiceSave(() => {
                                                            setSelectedInvoiceForModal(invoice);
                                                            setShowPaidOtherModal(true);
                                                        });
                                                    }}
                                                >
                                                    Paid By Other / Zero Bonus
                                                </button>
                                            )}

                                            <LessorCsvDownloadButton liTg={invoice.liTg} />

                                            <button
                                                style={styles.buttonSecondary}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedInvoiceForModal(invoice);
                                                    setRecordingModalOpen(true);
                                                }}
                                            >
                                                Recording Fees ({invoice.recordings?.length || 0})
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            <RecordingModal
                isOpen={isRecordingModalOpen}
                onClose={() => setRecordingModalOpen(false)}
                onSave={handleSaveRecordings}
                invoice={selectedInvoiceForModal}
                isEditable={isEditable && !!selectedInvoiceForModal}
                clNo={leaseMaster.clNo}
                prNo={leaseMaster.prNo}
                leaseNo={leaseMaster.lsNo}
                error={saveError}
            />

            <LeaseCollectionHistoryModal
                isOpen={isCollectionModalOpen}
                onClose={() => setIsCollectionModalOpen(false)}
                onSave={refreshInvoices}
                liTg={selectedInvoiceForModal?.liTg}
            />

            <LeaseDueDateModal
                isOpen={isDueDateModalOpen}
                onClose={() => setIsDueDateModalOpen(false)}
                onSave={refreshInvoices}
                liTg={selectedInvoiceForModal?.liTg}
            />
            <PaymentDueDateCalculatorModal
                isOpen={paymentDueDateCalculatorIndex !== null}
                onClose={() => setPaymentDueDateCalculatorIndex(null)}
                onConfirm={(dueDate) => {
                    if (paymentDueDateCalculatorIndex !== null) {
                        handleInvoiceChange(paymentDueDateCalculatorIndex, 'dueDate1', dueDate);
                    }
                    setPaymentDueDateCalculatorIndex(null);
                }}
            />
        </div>
    );
};

interface ITextAreaProps {
    label: string;
    value: string;
    isEditable: boolean;
    onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

const TextArea: React.FC<ITextAreaProps> = ({ label, value, isEditable, onChange }) => {
    const elementId = `textarea-${label.replace(/\s+/g, '-').toLowerCase()}`;
    return (
        <div>
            <label htmlFor={elementId} style={{ fontSize: styles.typography.fontSize.sm, color: styles.colors.gray700, fontWeight: 500 }}>
                {label}
            </label>
            <textarea
                id={elementId}
                value={value}
                disabled={!isEditable}
                onChange={onChange}
                style={{
                    width: '100%',
                    minHeight: '120px',
                    marginTop: '0.25rem',
                    padding: '0.75rem',
                    border: `1px solid ${styles.colors.gray300}`,
                    borderRadius: '6px',
                    fontSize: styles.typography.fontSize.base,
                    lineHeight: 1.5,
                    boxSizing: 'border-box',
                    resize: 'vertical',
                    backgroundColor: isEditable ? '#ffffff' : styles.colors.gray100,
                }}
            />
        </div>
    );
};

interface ICommentsPopoverProps {
    invoice: { comm1?: string; comm2?: string; attach?: string };
    isEditable: boolean;
    onClose: () => void;
    onSave: (comments: { comm1?: string; comm2?: string; attach?: string }) => Promise<boolean>;
}

const CommentsPopover: React.FC<ICommentsPopoverProps> = ({ invoice, isEditable, onClose, onSave }) => {
    const [isSaving, setIsSaving] = useState(false);
    const [localComments, setLocalComments] = useState({
        comm1: invoice.comm1 || '',
        comm2: invoice.comm2 || '',
        attach: invoice.attach || '',
    });

    useEffect(() => {
        setLocalComments({
            comm1: invoice.comm1 || '',
            comm2: invoice.comm2 || '',
            attach: invoice.attach || '',
        });
    }, [invoice]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape' && !isSaving) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isSaving, onClose]);

    const handleLocalChange = (field: 'comm1' | 'comm2' | 'attach', value: string): void => {
        setLocalComments((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async (): Promise<void> => {
        setIsSaving(true);
        const wasSaved = await onSave(localComments);
        setIsSaving(false);
        if (wasSaved) {
            onClose();
        }
    };

    return ReactDOM.createPortal(
        <div
            role="presentation"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !isSaving) {
                    onClose();
                }
            }}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1rem',
                backgroundColor: 'rgba(17, 24, 39, 0.5)',
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="invoice-comments-title"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
                style={{
                    width: 'min(720px, 100%)',
                    maxHeight: 'calc(100vh - 2rem)',
                    overflowY: 'auto',
                    backgroundColor: styles.colors.white,
                    border: `1px solid ${styles.colors.gray200}`,
                    borderRadius: '8px',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
                    padding: '1.5rem',
                    boxSizing: 'border-box',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                    <h4 id="invoice-comments-title" style={{ margin: 0, fontSize: styles.typography.fontSize.lg, color: styles.colors.gray900 }}>
                        Comments & Attachments
                    </h4>
                    <button
                        type="button"
                        aria-label="Close comments and attachments"
                        onClick={onClose}
                        disabled={isSaving}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: styles.colors.gray600,
                            cursor: isSaving ? 'not-allowed' : 'pointer',
                            fontSize: '1.5rem',
                            lineHeight: 1,
                            padding: '0.25rem',
                        }}
                    >
                        ×
                    </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <TextArea
                        label="Comment 1"
                        value={localComments.comm1}
                        isEditable={isEditable}
                        onChange={(e) => handleLocalChange('comm1', e.target.value)}
                    />
                    <TextArea
                        label="Comment 2"
                        value={localComments.comm2}
                        isEditable={isEditable}
                        onChange={(e) => handleLocalChange('comm2', e.target.value)}
                    />
                    <TextArea
                        label="Attachment Info"
                        value={localComments.attach}
                        isEditable={isEditable}
                        onChange={(e) => handleLocalChange('attach', e.target.value)}
                    />
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '0.75rem',
                        marginTop: '1.5rem',
                        paddingTop: '1rem',
                        borderTop: `1px solid ${styles.colors.gray200}`,
                    }}
                >
                    <button type="button" onClick={onClose} disabled={isSaving} style={styles.buttonSecondary}>
                        {isEditable ? 'Cancel' : 'Close'}
                    </button>
                    {isEditable && (
                        <button type="button" onClick={handleSave} disabled={isSaving} style={styles.buttonPrimary}>
                            {isSaving ? 'Saving...' : 'Save Comments'}
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

const mapStateToProps = (state: IRootState) => ({
    currentUser: selectors.auth.user(state),
});

export default connect(mapStateToProps)(InvoiceSection);



// WEBPACK FOOTER //
// ./src/components/leases/sections/Invoice.tsx